import { Box, CardBody, Flex } from '@chakra-ui/react';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { MouseEventHandler, ReactNode } from 'react';

import type { MetricValues, SecondaryDimensionValues } from '@revelio/core';
import { FormatType, getFormatter, TableChart } from '@revelio/replots';

import { usePrimaryFilterState } from '../../../filters/hooks/usePrimaryFilterState';
import { FilterId, type PrimaryFilterId } from '../../../filters/types/filters';
import { getColumnDimensionLabel } from '../../ChartConfig/utils/table-columns';
import { MultiMetricProvider } from '../../DataProvider/MultiMetricProvider';
import { DataProviderQueryStatus } from '../../DataProvider/types';
import type { TableColumnConfig } from '../../PageConfigProvider/types/item';
import { getEffectiveTableColumns } from '../../PageConfigProvider/utils/migrate-table-columns';
import { ChartFallback } from '../ChartFallback';
import { useChart } from '../ChartProvider';
import { ChartCard } from '../ChartRoot/Chart';
import { PrimaryEntitySwitcher } from '../chart-switcher/PrimaryEntitySwitcher';
import {
  type CustomTableRow,
  convertByMetricToTableRows,
} from './utils/convert-by-metric-to-table-rows';
import { deriveQueryFromColumns } from './utils/table-columns-to-query';

const columnRowKey = (
  column: TableColumnConfig,
  dimensionSource?: SecondaryDimensionValues
): string => {
  if (column.kind === 'variable') return column.metric;
  return column.dimension === dimensionSource
    ? 'dimension'
    : 'secondaryDimension';
};

const columnHeader = (column: TableColumnConfig): string => {
  if (column.label) return column.label;
  return column.kind === 'dimension'
    ? getColumnDimensionLabel(column.dimension)
    : column.metric;
};

/** Renders a variable cell with its configured number format (default: SI). */
const variableCell =
  (format: FormatType) =>
  (info: CellContext<CustomTableRow, unknown>): string => {
    const value = info.getValue();
    if (typeof value !== 'number') {
      return value === null || value === undefined ? '' : String(value);
    }
    return getFormatter(format)(value);
  };

const getDefaultFormat = (metric: MetricValues): FormatType => {
  switch (metric) {
    case 'headcount':
      return FormatType.INTEGER;
    case 'growth_rate':
    case 'hiring_rate':
    case 'attrition_rate':
      return FormatType.PERCENTAGE;
    case 'tenure':
      return FormatType.YEAR;
    case 'salary':
    case 'screener_salary':
      return FormatType.CURRENCY;
    default:
      return FormatType.DECIMAL;
  }
};

const buildColumnDefs = (
  columns: TableColumnConfig[],
  dimensionSource?: SecondaryDimensionValues
): ColumnDef<CustomTableRow, unknown>[] =>
  columns.map((column) => {
    const def: ColumnDef<CustomTableRow, unknown> = {
      id: column.id,
      accessorKey: columnRowKey(column, dimensionSource),
      header: columnHeader(column),
    };
    if (column.kind === 'variable') {
      const format = column.format ?? getDefaultFormat(column.metric);
      def.cell = variableCell(format);
    }
    return def;
  });

export const CustomTableChart = ({
  primaryDimension,
  onClick,
}: {
  primaryDimension?: PrimaryFilterId;
  onClick?: MouseEventHandler<HTMLDivElement>;
}) => {
  const { item } = useChart();

  const columns = getEffectiveTableColumns(
    item?.plot,
    primaryDimension ?? FilterId.COMPANY
  );
  const {
    dimensionSource,
    isDimensionSourcePrimary,
    secondaryDimension,
    metrics,
  } = deriveQueryFromColumns(columns, primaryDimension);

  const columnDefs = buildColumnDefs(columns, dimensionSource);

  const { primaryFilter: primaryFilterState } =
    usePrimaryFilterState(primaryDimension);

  const primaryEntityOptions =
    primaryFilterState?.primarySelections?.filter(
      (selection) => selection?.isActive
    ) ?? [];

  const switcher =
    !isDimensionSourcePrimary &&
    primaryDimension &&
    primaryEntityOptions?.length > 1 ? (
      <PrimaryEntitySwitcher
        isSingleEntityChart
        primaryFilterId={primaryDimension}
      />
    ) : null;

  return (
    <MultiMetricProvider
      primaryDimension={primaryDimension}
      dimensionSource={isDimensionSourcePrimary ? 'primary' : dimensionSource}
      metrics={metrics}
      secondaryDimension={secondaryDimension}
      chartType="table"
    >
      {({ data, status, error }) => {
        if (status !== DataProviderQueryStatus.Success) {
          return (
            <ChartFallback
              status={status}
              errorMessage={error?.message}
              onClick={onClick}
            />
          );
        }

        if (!data) {
          return (
            <ChartFallback
              status={DataProviderQueryStatus.Success}
              errorMessage="No data available"
              onClick={onClick}
            />
          );
        }

        return (
          <Table
            data={convertByMetricToTableRows(data.byMetric)}
            columns={columnDefs}
            header={switcher}
            onClick={onClick}
          />
        );
      }}
    </MultiMetricProvider>
  );
};

const Table = ({
  data,
  columns,
  header,
  onClick,
}: {
  data: CustomTableRow[];
  columns?: ColumnDef<CustomTableRow, unknown>[];
  header?: ReactNode;
  onClick?: MouseEventHandler<HTMLDivElement>;
}) => {
  return (
    <ChartCard onClick={onClick} position="relative">
      <CardBody height="100%">
        {header && (
          <Flex padding="0.5rem" width="100%" justifyContent="flex-end">
            <Box maxW="260px">{header}</Box>
          </Flex>
        )}
        <Box height={header ? 'calc(100% - 40px)' : '100%'}>
          <TableChart data={data} columns={columns} />
        </Box>
      </CardBody>
    </ChartCard>
  );
};
